import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Shield, Ban, UserX, AlertTriangle, Clock, Search, Gavel, Megaphone, Send, Sparkles, X, Smile, AtSign, Trash2, Link2, Radio, RefreshCw, Lightbulb, Check, ThumbsDown, Mic, MicOff, Headphones, VolumeX, LogOut as Disconnect, Users, Move } from 'lucide-react';
import Sidebar from '@/components/Sidebar';
import { useGuild } from '@/contexts/GuildContext';
import { toast } from '@/components/ui/sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ACTION_COLORS = { ban: '#EF4444', kick: '#F59E0B', warn: '#3B82F6', timeout: '#8B5CF6' };
const PIE_COLORS = ['#EF4444', '#F59E0B', '#3B82F6', '#8B5CF6', '#10B981'];
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('discord_token')}` } });

const ACTION_LABELS = { kick: 'Kick', ban: 'Ban', timeout: 'Timeout', warn: 'Warn' };

function FeedbackBanner({ status, testId }) {
  return (
    <AnimatePresence>
      {status && (
        <motion.div
          initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          className={`mt-3 p-3 rounded-xl ${status.ok ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'}`}
          data-testid={testId}
        >
          <p className={`text-sm font-semibold font-[Outfit] ${status.ok ? 'text-emerald-400' : 'text-red-400'}`}>{status.msg}</p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Take Action (kick/ban/timeout/warn from the dashboard) ──────────────────

function TakeActionForm({ guildId }) {
  const [query, setQuery] = useState('');
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState('warn');
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState(10);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const debounceRef = useRef(null);

  const search = useCallback((value) => {
    setQuery(value);
    setSelected(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) { setMembers([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/api/moderation/members/${guildId}`, { ...authHeader(), params: { q: value } });
        setMembers(data.members || []);
      } catch (e) {
        setMembers([]);
      } finally {
        setSearching(false);
      }
    }, 350);
  }, [guildId]);

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    setStatus(null);
    try {
      const { data } = await axios.post(`${API}/api/moderation/action`, {
        guild_id: guildId,
        member_id: selected.id,
        action,
        reason: reason.trim() || 'No reason provided',
        duration_minutes: Number(duration) || 10,
      }, authHeader());
      let msg = `${ACTION_LABELS[action]} applied to ${data.target_username}`;
      if (action === 'warn') {
        msg += data.dm_delivered ? ' — DM delivered' : ' — DM could not be delivered (their DMs are closed)';
      }
      setStatus({ ok: true, msg });
      setSelected(null);
      setQuery('');
      setMembers([]);
      setReason('');
    } catch (err) {
      setStatus({ ok: false, msg: err.response?.data?.detail || 'Action failed.' });
    } finally {
      setSubmitting(false);
      setTimeout(() => setStatus(null), 5000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden"
      data-testid="take-action-form"
    >
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-[var(--border)]">
        <Gavel size={15} className="text-[#5865F2]" />
        <span className="text-sm font-semibold text-white font-[Outfit]">Take Action</span>
        <span className="text-xs text-zinc-600 font-[Manrope] ml-1">— moderate without slash commands</span>
      </div>
      <div className="p-4 space-y-3">
        <div className="relative">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input
            value={query}
            onChange={e => search(e.target.value)}
            placeholder="Search a member by username..."
            data-testid="member-search-input"
            className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 focus:bg-white/8 transition-all font-[Manrope]"
          />
          {(query && !selected) && (
            <div className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-xl shadow-xl">
              {searching ? (
                <p className="px-4 py-3 text-xs text-zinc-500 font-[Manrope]">Searching...</p>
              ) : members.length === 0 ? (
                <p className="px-4 py-3 text-xs text-zinc-500 font-[Manrope]">No members found</p>
              ) : members.map(m => (
                <button
                  key={m.id}
                  onClick={() => { setSelected(m); setQuery(m.username); setMembers([]); }}
                  data-testid={`member-option-${m.id}`}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 hover:bg-[var(--bg-hover)] transition-colors text-left"
                >
                  {m.avatar_url && <img src={m.avatar_url} alt="" className="w-6 h-6 rounded-full" />}
                  <span className="text-sm text-white font-[Manrope]">{m.display_name}</span>
                  <span className="text-xs text-zinc-500 font-[Manrope]">{m.username}</span>
                  {m.is_owner && <span className="ml-auto text-xs text-amber-400 font-[Manrope]">Owner</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <select
            value={action}
            onChange={e => setAction(e.target.value)}
            data-testid="action-select"
            className="bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
          >
            <option value="warn" className="bg-[var(--bg-surface)]">Warn</option>
            <option value="timeout" className="bg-[var(--bg-surface)]">Timeout</option>
            <option value="kick" className="bg-[var(--bg-surface)]">Kick</option>
            <option value="ban" className="bg-[var(--bg-surface)]">Ban</option>
          </select>
          {action === 'timeout' ? (
            <input
              type="number" min={1} max={40320} value={duration}
              onChange={e => setDuration(e.target.value)}
              placeholder="Minutes"
              data-testid="timeout-duration-input"
              className="bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
            />
          ) : <div />}
        </div>

        <input
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Reason (optional)"
          data-testid="action-reason-input"
          className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
        />

        <button
          onClick={submit}
          disabled={!selected || submitting}
          data-testid="take-action-submit"
          className="w-full px-5 py-2.5 bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all font-[Outfit] flex items-center justify-center gap-2"
        >
          {submitting
            ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Applying...</>
            : <><Gavel size={14} />{ACTION_LABELS[action]}{selected ? ` ${selected.display_name}` : ''}</>
          }
        </button>

        <FeedbackBanner status={status} testId="take-action-feedback" />
      </div>
    </motion.div>
  );
}

// ─── Send Announcement ────────────────────────────────────────────────────────

function ScheduledAnnouncements({ guildId, refreshKey }) {
  const [items, setItems] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const load = useCallback(() => {
    if (!guildId) return;
    axios.get(`${API}/api/announce/scheduled`, { ...authHeader(), params: { guild_id: guildId } })
      .then(r => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [guildId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const cancel = async (id) => {
    setCancelling(id);
    try {
      await axios.delete(`${API}/api/announce/scheduled/${id}`, { ...authHeader(), params: { guild_id: guildId } });
      setItems(prev => prev.filter(i => i.id !== id));
    } catch (e) {
      // ignore
    } finally {
      setCancelling(null);
    }
  };

  if (!items || items.length === 0) return null;

  const STATUS_STYLES = {
    pending: 'bg-amber-500/15 text-amber-400 border border-amber-500/30',
    sent: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30',
    failed: 'bg-red-500/15 text-red-400 border border-red-500/30',
  };

  return (
    <div className="mt-4 pt-4 border-t border-[var(--border)] space-y-2" data-testid="scheduled-announcements-list">
      <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest font-[Manrope]">Scheduled</p>
      {items.map(item => (
        <div key={item.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[var(--bg-hover)]" data-testid={`scheduled-item-${item.id}`}>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium font-[Manrope] flex-shrink-0 ${STATUS_STYLES[item.status] || ''}`}>
            {item.status}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate font-[Manrope]">{item.title || 'Announcement'} → #{item.channel_name}</p>
            <p className="text-[11px] text-zinc-500">{new Date(item.send_at).toLocaleString()}</p>
          </div>
          {item.status === 'pending' && (
            <button
              onClick={() => cancel(item.id)}
              disabled={cancelling === item.id}
              data-testid={`cancel-scheduled-${item.id}`}
              className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50 transition-colors flex-shrink-0"
            >
              Cancel
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function AnnouncementForm({ guildId }) {
  const [channels, setChannels] = useState(null);
  const [channelId, setChannelId] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [sendAt, setSendAt] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/announce/channels/${guildId}`, authHeader()),
      axios.get(`${API}/api/settings/${guildId}`, authHeader()).catch(() => ({ data: {} })),
    ]).then(([chRes, settingsRes]) => {
      const chs = chRes.data.channels || [];
      setChannels(chs);
      const preferred = settingsRes.data?.announce_channel_id;
      setChannelId((preferred && chs.some(c => c.id === preferred)) ? preferred : (chs[0]?.id || ''));
    }).catch(() => setChannels([]));
  }, [guildId]);

  const submit = async () => {
    if (!channelId || !message.trim()) return;
    if (scheduleEnabled && !sendAt) return;
    setSending(true);
    setStatus(null);
    try {
      if (scheduleEnabled) {
        await axios.post(`${API}/api/announce/schedule`, {
          guild_id: guildId, channel_id: channelId, message: message.trim(),
          title: title.trim() || undefined, send_at: new Date(sendAt).toISOString(),
        }, authHeader());
        setStatus({ ok: true, msg: 'Announcement scheduled' });
        setSendAt('');
        setRefreshKey(k => k + 1);
      } else {
        const { data } = await axios.post(`${API}/api/announce`, {
          guild_id: guildId, channel_id: channelId, message: message.trim(), title: title.trim() || undefined,
        }, authHeader());
        setStatus({ ok: true, msg: `Sent to #${data.channel_name}` });
      }
      setMessage('');
      setTitle('');
    } catch (err) {
      setStatus({ ok: false, msg: err.response?.data?.detail || 'Could not send announcement.' });
    } finally {
      setSending(false);
      setTimeout(() => setStatus(null), 5000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden"
      data-testid="announcement-form"
    >
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-[var(--border)]">
        <Megaphone size={15} className="text-[#5865F2]" />
        <span className="text-sm font-semibold text-white font-[Outfit]">Send Announcement</span>
        <span className="text-xs text-zinc-600 font-[Manrope] ml-1">— post to any channel from here</span>
      </div>
      <div className="p-4 space-y-3">
        {channels === null ? (
          <p className="text-zinc-600 text-sm font-[Manrope]">Loading channels...</p>
        ) : channels.length === 0 ? (
          <p className="text-zinc-600 text-sm font-[Manrope]">No text channels found.</p>
        ) : (
          <select
            value={channelId}
            onChange={e => setChannelId(e.target.value)}
            data-testid="announce-channel-select"
            className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
          >
            {channels.map(ch => (
              <option key={ch.id} value={ch.id} className="bg-[var(--bg-surface)]">#{ch.name}</option>
            ))}
          </select>
        )}
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Title (optional)"
          data-testid="announce-title-input"
          className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
        />
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Write your announcement..."
          rows={4}
          data-testid="announce-message-input"
          className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope] resize-none"
        />
        <label className="flex items-center gap-2 text-sm text-zinc-400 font-[Manrope] cursor-pointer select-none">
          <input
            type="checkbox"
            checked={scheduleEnabled}
            onChange={e => setScheduleEnabled(e.target.checked)}
            data-testid="schedule-toggle"
            className="rounded border-white/20 bg-[var(--bg-hover)] accent-[#5865F2]"
          />
          Schedule for later
        </label>
        {scheduleEnabled && (
          <input
            type="datetime-local"
            value={sendAt}
            onChange={e => setSendAt(e.target.value)}
            data-testid="schedule-datetime-input"
            min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
            className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
          />
        )}
        <button
          onClick={submit}
          disabled={!channelId || !message.trim() || sending || (scheduleEnabled && !sendAt)}
          data-testid="announce-submit"
          className="w-full px-5 py-2.5 bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all font-[Outfit] flex items-center justify-center gap-2"
        >
          {sending
            ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />{scheduleEnabled ? 'Scheduling...' : 'Sending...'}</>
            : <><Send size={14} />{scheduleEnabled ? 'Schedule Announcement' : 'Send Announcement'}</>
          }
        </button>
        <FeedbackBanner status={status} testId="announce-feedback" />
        <ScheduledAnnouncements guildId={guildId} refreshKey={refreshKey} />
      </div>
    </motion.div>
  );
}

function EmbedStudio({ guildId }) {
  const [channels, setChannels] = useState(null);
  const [channelId, setChannelId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#5865F2');
  const [imageUrl, setImageUrl] = useState('');
  const [footerText, setFooterText] = useState('');
  const [fields, setFields] = useState([]);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/announce/channels/${guildId}`, authHeader())
      .then(r => {
        const chs = r.data.channels || [];
        setChannels(chs);
        setChannelId(chs[0]?.id || '');
      })
      .catch(() => setChannels([]));
  }, [guildId]);

  const addField = () => {
    if (fields.length >= 5) return;
    setFields(f => [...f, { name: '', value: '', inline: false }]);
  };
  const updateField = (i, patch) => setFields(f => f.map((fl, idx) => idx === i ? { ...fl, ...patch } : fl));
  const removeField = (i) => setFields(f => f.filter((_, idx) => idx !== i));

  const isValidColor = /^#?[0-9a-fA-F]{6}$/.test(color);
  const swatch = isValidColor ? (color.startsWith('#') ? color : `#${color}`) : '#5865F2';

  const submit = async () => {
    if (!channelId || (!title.trim() && !description.trim())) return;
    setSending(true);
    setStatus(null);
    try {
      const { data } = await axios.post(`${API}/api/announce/embed`, {
        guild_id: guildId,
        channel_id: channelId,
        title: title.trim() || undefined,
        description: description.trim() || undefined,
        color: isValidColor ? swatch : undefined,
        image_url: imageUrl.trim() || undefined,
        footer_text: footerText.trim() || undefined,
        fields: fields.filter(f => f.name.trim() && f.value.trim()),
      }, authHeader());
      setStatus({ ok: true, msg: `Sent to #${data.channel_name}` });
      setTitle('');
      setDescription('');
      setImageUrl('');
      setFooterText('');
      setFields([]);
    } catch (err) {
      setStatus({ ok: false, msg: err.response?.data?.detail || 'Could not send embed.' });
    } finally {
      setSending(false);
      setTimeout(() => setStatus(null), 5000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden mb-6"
      data-testid="embed-studio"
    >
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-[var(--border)]">
        <Sparkles size={15} className="text-[#5865F2]" />
        <span className="text-sm font-semibold text-white font-[Outfit]">Embed Studio</span>
        <span className="text-xs text-zinc-600 font-[Manrope] ml-1">— design a rich embed and dispatch it instantly</span>
      </div>

      <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Builder */}
        <div className="space-y-3">
          {channels === null ? (
            <p className="text-zinc-600 text-sm font-[Manrope]">Loading channels...</p>
          ) : channels.length === 0 ? (
            <p className="text-zinc-600 text-sm font-[Manrope]">No text channels found.</p>
          ) : (
            <select
              value={channelId}
              onChange={e => setChannelId(e.target.value)}
              data-testid="embed-channel-select"
              className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
            >
              {channels.map(ch => (
                <option key={ch.id} value={ch.id} className="bg-[var(--bg-surface)]">#{ch.name}</option>
              ))}
            </select>
          )}
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Embed title"
            data-testid="embed-title-input"
            className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
          />
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Embed description / body text..."
            rows={4}
            data-testid="embed-description-input"
            className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope] resize-none"
          />
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg border border-[var(--border-md)] flex-shrink-0" style={{ background: swatch }} />
            <input
              value={color}
              onChange={e => setColor(e.target.value)}
              placeholder="#5865F2"
              data-testid="embed-color-input"
              className={`flex-1 bg-[var(--bg-hover)] border rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none transition-all font-mono-stats ${isValidColor ? 'border-[var(--border)] focus:border-[#5865F2]/50' : 'border-red-500/40'}`}
            />
          </div>
          {!isValidColor && color && <p className="text-xs text-red-400 font-[Manrope]">Enter a valid hex color, e.g. #5865F2</p>}
          <input
            value={imageUrl}
            onChange={e => setImageUrl(e.target.value)}
            placeholder="Image URL (optional)"
            data-testid="embed-image-input"
            className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
          />
          <input
            value={footerText}
            onChange={e => setFooterText(e.target.value)}
            placeholder="Footer text (optional)"
            data-testid="embed-footer-input"
            className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
          />

          {/* Fields */}
          <div className="space-y-2">
            {fields.map((f, i) => (
              <div key={i} className="flex flex-col gap-1.5 p-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)]">
                <div className="flex gap-1.5">
                  <input
                    value={f.name}
                    onChange={e => updateField(i, { name: e.target.value })}
                    placeholder="Field name"
                    data-testid={`embed-field-name-${i}`}
                    className="flex-1 bg-[var(--bg-hover)] border border-[var(--border)] rounded-lg px-3 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
                  />
                  <button onClick={() => removeField(i)} data-testid={`embed-field-remove-${i}`} className="text-zinc-500 hover:text-red-400 transition-colors px-1.5">
                    <X size={14} />
                  </button>
                </div>
                <input
                  value={f.value}
                  onChange={e => updateField(i, { value: e.target.value })}
                  placeholder="Field value"
                  data-testid={`embed-field-value-${i}`}
                  className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-lg px-3 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
                />
                <label className="flex items-center gap-1.5 text-xs text-zinc-500 font-[Manrope] cursor-pointer select-none">
                  <input type="checkbox" checked={f.inline} onChange={e => updateField(i, { inline: e.target.checked })} className="rounded border-white/20 bg-[var(--bg-hover)] accent-[#5865F2]" />
                  Inline
                </label>
              </div>
            ))}
            {fields.length < 5 && (
              <button onClick={addField} data-testid="embed-add-field" className="text-xs text-[#5865F2] hover:text-[#7983F5] transition-colors font-[Manrope]">
                + Add field
              </button>
            )}
          </div>

          <button
            onClick={submit}
            disabled={!channelId || (!title.trim() && !description.trim()) || sending}
            data-testid="embed-send-btn"
            className="w-full px-5 py-2.5 bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all font-[Outfit] flex items-center justify-center gap-2"
          >
            {sending
              ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending...</>
              : <><Send size={14} />Send to Channel</>
            }
          </button>
          <FeedbackBanner status={status} testId="embed-feedback" />
        </div>

        {/* Live Preview */}
        <div>
          <p className="text-xs text-zinc-500 uppercase tracking-widest mb-2 font-[Manrope]">Live Preview</p>
          <div className="bg-[#36393f] rounded-xl p-4" data-testid="embed-preview">
            <div className="flex gap-3 bg-[#2f3136] rounded-lg overflow-hidden" style={{ borderLeft: `4px solid ${swatch}` }}>
              <div className="p-3.5 flex-1 min-w-0">
                {title.trim() && <p className="text-white font-semibold text-sm mb-1 break-words">{title}</p>}
                {description.trim() && <p className="text-[#dcddde] text-sm whitespace-pre-wrap break-words">{description}</p>}
                {fields.filter(f => f.name.trim() && f.value.trim()).length > 0 && (
                  <div className="grid grid-cols-2 gap-2 mt-2.5">
                    {fields.filter(f => f.name.trim() && f.value.trim()).map((f, i) => (
                      <div key={i} className={f.inline ? '' : 'col-span-2'}>
                        <p className="text-white text-xs font-semibold">{f.name}</p>
                        <p className="text-[#dcddde] text-xs whitespace-pre-wrap break-words">{f.value}</p>
                      </div>
                    ))}
                  </div>
                )}
                {imageUrl.trim() && (
                  <img src={imageUrl} alt="" className="mt-2.5 rounded-lg max-h-48 max-w-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                )}
                <div className="flex items-center gap-1.5 mt-2.5">
                  <p className="text-[#a3a6aa] text-[11px]">{footerText.trim() || 'Sent by Dashboard User via BeatMetrics'} • {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>
            </div>
            {!title.trim() && !description.trim() && (
              <p className="text-zinc-500 text-xs text-center py-6 font-[Manrope]">Start typing to preview your embed</p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

const MESSAGE_LINK_RE = /discord(?:app)?\.com\/channels\/(\d+)\/(\d+)\/(\d+)/;

function ReactionRolesBuilder({ guildId }) {
  const [channels, setChannels] = useState(null);
  const [roles, setRoles] = useState(null);
  const [bindings, setBindings] = useState(null);
  const [channelId, setChannelId] = useState('');
  const [messageRef, setMessageRef] = useState('');
  const [emoji, setEmoji] = useState('');
  const [roleId, setRoleId] = useState('');
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [status, setStatus] = useState(null);

  const loadBindings = useCallback(() => {
    axios.get(`${API}/api/reaction-roles/${guildId}`, authHeader())
      .then(r => setBindings(r.data.bindings || []))
      .catch(() => setBindings([]));
  }, [guildId]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/announce/channels/${guildId}`, authHeader()),
      axios.get(`${API}/api/reaction-roles/${guildId}/roles`, authHeader()),
    ]).then(([chRes, roleRes]) => {
      const chs = chRes.data.channels || [];
      setChannels(chs);
      setChannelId(chs[0]?.id || '');
      const rls = roleRes.data.roles || [];
      setRoles(rls);
      setRoleId(rls[0]?.id || '');
    }).catch(() => { setChannels([]); setRoles([]); });
    loadBindings();
  }, [guildId, loadBindings]);

  const submit = async () => {
    const ref = messageRef.trim();
    if (!ref || !emoji.trim() || !roleId) return;
    let targetChannelId = channelId;
    let messageId = ref;
    const linkMatch = ref.match(MESSAGE_LINK_RE);
    if (linkMatch) {
      targetChannelId = linkMatch[2];
      messageId = linkMatch[3];
    }
    if (!/^\d+$/.test(messageId)) {
      setStatus({ ok: false, msg: 'Enter a valid message ID or message link.' });
      setTimeout(() => setStatus(null), 4000);
      return;
    }
    setCreating(true);
    setStatus(null);
    try {
      await axios.post(`${API}/api/reaction-roles`, {
        guild_id: guildId, channel_id: targetChannelId, message_id: messageId,
        emoji: emoji.trim(), role_id: roleId,
      }, authHeader());
      setStatus({ ok: true, msg: 'Binding created — the bot reacted to the message!' });
      setMessageRef('');
      setEmoji('');
      loadBindings();
    } catch (err) {
      setStatus({ ok: false, msg: err.response?.data?.detail || 'Could not create binding.' });
    } finally {
      setCreating(false);
      setTimeout(() => setStatus(null), 5000);
    }
  };

  const removeBinding = async (id) => {
    setRemoving(id);
    try {
      await axios.delete(`${API}/api/reaction-roles/${id}`, { ...authHeader(), params: { guild_id: guildId } });
      setBindings(prev => prev.filter(b => b.id !== id));
    } catch (e) {
      // ignore
    } finally {
      setRemoving(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden mb-6"
      data-testid="reaction-roles-builder"
    >
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-[var(--border)]">
        <AtSign size={15} className="text-[#5865F2]" />
        <span className="text-sm font-semibold text-white font-[Outfit]">Reaction Roles</span>
        <span className="text-xs text-zinc-600 font-[Manrope] ml-1">— bind an emoji on a message to a role</span>
      </div>

      <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Builder */}
        <div className="space-y-3">
          <div className="flex items-start gap-2 text-xs text-zinc-500 font-[Manrope] bg-[var(--bg-hover)] rounded-lg p-2.5">
            <Link2 size={14} className="text-[#5865F2] flex-shrink-0 mt-0.5" />
            <span>Right-click any message in Discord → <strong className="text-zinc-400">Copy Message Link</strong> (or Copy ID with Developer Mode), then paste it below.</span>
          </div>

          {channels === null ? (
            <p className="text-zinc-600 text-sm font-[Manrope]">Loading channels...</p>
          ) : (
            <select
              value={channelId}
              onChange={e => setChannelId(e.target.value)}
              data-testid="rr-channel-select"
              className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
            >
              {channels.map(ch => (
                <option key={ch.id} value={ch.id} className="bg-[var(--bg-surface)]">#{ch.name}</option>
              ))}
            </select>
          )}
          <input
            value={messageRef}
            onChange={e => setMessageRef(e.target.value)}
            placeholder="Message link or message ID"
            data-testid="rr-message-input"
            className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all font-mono-stats"
          />
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Smile size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                value={emoji}
                onChange={e => setEmoji(e.target.value)}
                placeholder="Emoji (e.g. 🎮 or :customname:)"
                data-testid="rr-emoji-input"
                className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
              />
            </div>
          </div>
          {roles === null ? (
            <p className="text-zinc-600 text-sm font-[Manrope]">Loading roles...</p>
          ) : roles.length === 0 ? (
            <p className="text-zinc-600 text-sm font-[Manrope]">No assignable roles found. Create a role below the bot's role first.</p>
          ) : (
            <select
              value={roleId}
              onChange={e => setRoleId(e.target.value)}
              data-testid="rr-role-select"
              className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
            >
              {roles.map(r => (
                <option key={r.id} value={r.id} className="bg-[var(--bg-surface)]">@{r.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={submit}
            disabled={!messageRef.trim() || !emoji.trim() || !roleId || creating}
            data-testid="rr-create-btn"
            className="w-full px-5 py-2.5 bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all font-[Outfit] flex items-center justify-center gap-2"
          >
            {creating
              ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Binding...</>
              : <><Sparkles size={14} />Bind Reaction to Role</>
            }
          </button>
          <FeedbackBanner status={status} testId="rr-feedback" />
        </div>

        {/* Active bindings */}
        <div>
          <p className="text-xs text-zinc-500 uppercase tracking-widest mb-2 font-[Manrope]">Active Bindings</p>
          {bindings === null ? (
            <p className="text-zinc-600 text-sm font-[Manrope]">Loading...</p>
          ) : bindings.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-10 font-[Manrope] bg-[var(--bg-hover)] rounded-xl">No reaction roles set up yet.</p>
          ) : (
            <div className="space-y-2 max-h-[26rem] overflow-y-auto pr-1">
              {bindings.map(b => (
                <div key={b.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)]" data-testid={`rr-binding-${b.id}`}>
                  <span className="text-xl flex-shrink-0">{b.emoji_display || b.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-[Manrope] truncate">
                      grants <span className="font-semibold" style={{ color: b.role_color || '#A1A1AA' }}>@{b.role_name}</span>
                    </p>
                    <p className="text-xs text-zinc-500 font-mono-stats truncate">message {b.message_id}</p>
                  </div>
                  <button
                    onClick={() => removeBinding(b.id)}
                    disabled={removing === b.id}
                    data-testid={`rr-remove-${b.id}`}
                    className="text-zinc-500 hover:text-red-400 disabled:opacity-50 transition-colors p-1.5 flex-shrink-0"
                  >
                    {removing === b.id ? <div className="w-3.5 h-3.5 border-2 border-zinc-600 border-t-transparent rounded-full animate-spin" /> : <Trash2 size={14} />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function SuggestionsBoard({ guildId }) {
  const [suggestions, setSuggestions] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [updating, setUpdating] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/api/suggestions/${guildId}`, authHeader())
      .then(r => setSuggestions(r.data.suggestions || []))
      .catch(() => setSuggestions([]));
  }, [guildId]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (messageId, status) => {
    setUpdating(messageId);
    try {
      await axios.post(`${API}/api/suggestions/${guildId}/${messageId}/status`, { status }, authHeader());
      setSuggestions(prev => prev.map(s => s.message_id === messageId ? { ...s, status } : s));
    } catch (e) {
      // ignore
    } finally {
      setUpdating(null);
    }
  };

  const filtered = (suggestions || []).filter(s => filter === 'all' || (s.status || 'pending') === filter);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden mb-6"
      data-testid="suggestions-board"
    >
      <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Lightbulb size={16} className="text-amber-400" />
          <h3 className="text-sm font-semibold text-white font-[Outfit]">Suggestion Box</h3>
        </div>
        <div className="flex items-center gap-1.5">
          {['pending', 'approved', 'rejected', 'all'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`suggestion-filter-${f}`}
              className={`text-xs px-2.5 py-1 rounded-lg capitalize transition-colors ${filter === f ? 'bg-[#5865F2] text-white' : 'bg-[var(--bg-hover)] text-zinc-400 hover:text-white'}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>
      <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
        {suggestions === null ? (
          <p className="text-zinc-500 text-sm text-center py-8">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-8">No suggestions here.</p>
        ) : (
          filtered.map(s => (
            <div key={s.message_id} className="flex items-start gap-3 px-3 py-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)]" data-testid={`suggestion-${s.message_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-[Manrope]">{s.suggestion}</p>
                <p className="text-xs text-zinc-500 mt-1">by {s.author_username} · {new Date(s.timestamp).toLocaleString()}</p>
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium capitalize flex-shrink-0 ${
                s.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400' :
                s.status === 'rejected' ? 'bg-red-500/15 text-red-400' : 'bg-zinc-700/50 text-zinc-400'
              }`}>
                {s.status || 'pending'}
              </span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => setStatus(s.message_id, 'approved')}
                  disabled={updating === s.message_id}
                  title="Approve"
                  data-testid={`approve-${s.message_id}`}
                  className="p-1.5 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/30 transition-colors disabled:opacity-40"
                >
                  <Check size={13} />
                </button>
                <button
                  onClick={() => setStatus(s.message_id, 'rejected')}
                  disabled={updating === s.message_id}
                  title="Reject"
                  data-testid={`reject-${s.message_id}`}
                  className="p-1.5 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] text-zinc-400 hover:text-red-400 hover:border-red-500/30 transition-colors disabled:opacity-40"
                >
                  <ThumbsDown size={13} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </motion.div>
  );
}

function BanAppealsPanel({ guildId }) {
  const [appeals, setAppeals] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [deciding, setDeciding] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/api/ban-appeals/${guildId}`, authHeader())
      .then(r => setAppeals(r.data.appeals || []))
      .catch(() => setAppeals([]));
  }, [guildId]);

  useEffect(() => { load(); }, [load]);

  const decide = async (userId, decision) => {
    setDeciding(userId);
    try {
      await axios.post(`${API}/api/ban-appeals/${guildId}/${userId}/decide`, { decision }, authHeader());
      setAppeals(prev => prev.map(a => a.user_id === userId ? { ...a, status: decision === 'approve' ? 'approved' : 'rejected' } : a));
    } catch (e) {
      // ignore
    } finally {
      setDeciding(null);
    }
  };

  const filtered = (appeals || []).filter(a => filter === 'all' || (a.status || 'pending') === filter);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden mb-6"
      data-testid="ban-appeals-panel"
    >
      <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Gavel size={16} className="text-red-400" />
          <h3 className="text-sm font-semibold text-white font-[Outfit]">Ban Appeals</h3>
        </div>
        <div className="flex items-center gap-1.5">
          {['pending', 'approved', 'rejected', 'all'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`appeal-filter-${f}`}
              className={`text-xs px-2.5 py-1 rounded-lg capitalize transition-colors ${filter === f ? 'bg-[#5865F2] text-white' : 'bg-[var(--bg-hover)] text-zinc-400 hover:text-white'}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>
      <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
        {appeals === null ? (
          <p className="text-zinc-500 text-sm text-center py-8">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-8">No ban appeals here.</p>
        ) : (
          filtered.map(a => (
            <div key={a.user_id} className="flex items-start gap-3 px-3 py-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)]" data-testid={`appeal-${a.user_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium font-[Manrope]">{a.username}</p>
                {a.ban_reason && <p className="text-xs text-zinc-500 mt-0.5">Ban reason: {a.ban_reason}</p>}
                <p className="text-sm text-zinc-300 font-[Manrope] mt-1">{a.reason}</p>
                <p className="text-xs text-zinc-500 mt-1">{new Date(a.timestamp).toLocaleString()}</p>
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium capitalize flex-shrink-0 ${
                a.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400' :
                a.status === 'rejected' ? 'bg-red-500/15 text-red-400' : 'bg-zinc-700/50 text-zinc-400'
              }`}>
                {a.status || 'pending'}
              </span>
              {(a.status || 'pending') === 'pending' && (
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => decide(a.user_id, 'approve')}
                    disabled={deciding === a.user_id}
                    title="Approve & Unban"
                    data-testid={`appeal-approve-${a.user_id}`}
                    className="p-1.5 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/30 transition-colors disabled:opacity-40"
                  >
                    <Check size={13} />
                  </button>
                  <button
                    onClick={() => decide(a.user_id, 'reject')}
                    disabled={deciding === a.user_id}
                    title="Reject"
                    data-testid={`appeal-reject-${a.user_id}`}
                    className="p-1.5 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] text-zinc-400 hover:text-red-400 hover:border-red-500/30 transition-colors disabled:opacity-40"
                  >
                    <ThumbsDown size={13} />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </motion.div>
  );
}

function LiveVoiceMonitor({ guildId }) {
  const [channels, setChannels] = useState(null);
  const [allChannels, setAllChannels] = useState([]);
  const [acting, setActing] = useState(null);
  const [moveTarget, setMoveTarget] = useState({});

  const load = useCallback(() => {
    axios.get(`${API}/api/voice-monitor/${guildId}`, authHeader())
      .then(r => {
        setChannels(r.data.channels || []);
        setAllChannels(r.data.all_channels || []);
      })
      .catch(() => setChannels([]));
  }, [guildId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [load]);

  const doAction = async (userId, action, targetChannelId = null) => {
    setActing(`${userId}-${action}`);
    try {
      await axios.post(`${API}/api/voice-monitor/action`, { guild_id: guildId, user_id: userId, action, target_channel_id: targetChannelId }, authHeader());
      load();
    } catch (e) {
      // ignore
    } finally {
      setActing(null);
    }
  };

  const allChannelOptions = allChannels;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden mb-6"
      data-testid="live-voice-monitor"
    >
      <div className="px-5 py-4 border-b border-[var(--border)] flex items-center gap-2">
        <Users size={16} className="text-emerald-400" />
        <h3 className="text-sm font-semibold text-white font-[Outfit]">Live Voice Channels</h3>
      </div>
      <div className="p-4 space-y-4">
        {channels === null ? (
          <p className="text-zinc-500 text-sm text-center py-8">Loading...</p>
        ) : channels.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-8">No one is in a voice channel right now.</p>
        ) : (
          channels.map(ch => (
            <div key={ch.id} data-testid={`voice-channel-${ch.id}`}>
              <p className="text-xs text-zinc-500 uppercase tracking-widest font-[Manrope] mb-2">{ch.name} · {ch.members.length}</p>
              <div className="space-y-1.5">
                {ch.members.map(m => (
                  <div key={m.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)]" data-testid={`voice-member-${m.id}`}>
                    {m.avatar ? <img src={m.avatar} alt="" className="w-7 h-7 rounded-full" /> : <div className="w-7 h-7 rounded-full bg-[#5865F2]/20" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-[Manrope] truncate">{m.display_name}{m.bot ? ' (bot)' : ''}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {(m.mute || m.self_mute) && <span className="text-[10px] text-amber-400 flex items-center gap-0.5"><MicOff size={10} /> Muted</span>}
                        {(m.deaf || m.self_deaf) && <span className="text-[10px] text-red-400 flex items-center gap-0.5"><VolumeX size={10} /> Deafened</span>}
                        {m.self_stream && <span className="text-[10px] text-violet-400">Streaming</span>}
                      </div>
                    </div>
                    {!m.bot && (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => doAction(m.id, m.mute ? 'unmute' : 'mute')}
                          disabled={acting === `${m.id}-mute` || acting === `${m.id}-unmute`}
                          title={m.mute ? 'Server-unmute' : 'Server-mute'}
                          data-testid={`mute-${m.id}`}
                          className={`p-1.5 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] transition-colors disabled:opacity-40 ${m.mute ? 'text-amber-400' : 'text-zinc-400 hover:text-amber-400'}`}
                        >
                          {m.mute ? <MicOff size={13} /> : <Mic size={13} />}
                        </button>
                        <button
                          onClick={() => doAction(m.id, m.deaf ? 'undeafen' : 'deafen')}
                          disabled={acting === `${m.id}-deafen` || acting === `${m.id}-undeafen`}
                          title={m.deaf ? 'Server-undeafen' : 'Server-deafen'}
                          data-testid={`deafen-${m.id}`}
                          className={`p-1.5 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] transition-colors disabled:opacity-40 ${m.deaf ? 'text-red-400' : 'text-zinc-400 hover:text-red-400'}`}
                        >
                          <Headphones size={13} />
                        </button>
                        <select
                          value={moveTarget[m.id] || ''}
                          onChange={e => setMoveTarget(prev => ({ ...prev, [m.id]: e.target.value }))}
                          data-testid={`move-select-${m.id}`}
                          className="bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-1.5 py-1 text-xs text-zinc-300 focus:outline-none"
                        >
                          <option value="">Move to...</option>
                          {allChannelOptions.filter(c => c.id !== ch.id).map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => moveTarget[m.id] && doAction(m.id, 'move', moveTarget[m.id])}
                          disabled={!moveTarget[m.id] || acting === `${m.id}-move`}
                          title="Move to selected channel"
                          data-testid={`move-${m.id}`}
                          className="p-1.5 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] text-zinc-400 hover:text-[#5865F2] transition-colors disabled:opacity-40"
                        >
                          <Move size={13} />
                        </button>
                        <button
                          onClick={() => doAction(m.id, 'disconnect')}
                          disabled={acting === `${m.id}-disconnect`}
                          title="Disconnect from voice"
                          data-testid={`disconnect-${m.id}`}
                          className="p-1.5 rounded-lg border border-[var(--border)] bg-[var(--bg-hover)] text-zinc-400 hover:text-red-400 transition-colors disabled:opacity-40"
                        >
                          <Disconnect size={13} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </motion.div>
  );
}

function BotVoiceHealth({ guildId }) {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    const load = () => axios.get(`${API}/api/voice-disconnects/${guildId}`, authHeader())
      .then(r => setEvents(r.data.events || []))
      .catch(() => setEvents([]));
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [guildId]);

  if (events !== null && events.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
      className="bg-[var(--bg-surface)] border border-amber-500/20 rounded-xl overflow-hidden mb-6"
      data-testid="bot-voice-health"
    >
      <div className="px-5 py-4 border-b border-[var(--border)] flex items-center gap-2">
        <VolumeX size={16} className="text-amber-400" />
        <h3 className="text-sm font-semibold text-white font-[Outfit]">Recent Voice Disconnects</h3>
      </div>
      <div className="p-4 space-y-1.5">
        {events === null ? (
          <p className="text-zinc-500 text-sm text-center py-4">Loading...</p>
        ) : (
          events.map((e, i) => (
            <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)]" data-testid={`voice-disconnect-${i}`}>
              <div className="min-w-0">
                <p className="text-sm text-white font-[Manrope] truncate">{e.channel_name}{e.track_title ? ` — was playing "${e.track_title}"` : ''}</p>
                <p className="text-xs text-zinc-500 font-[Manrope]">{new Date(e.timestamp).toLocaleString()}</p>
              </div>
              <span className={`text-[11px] px-2 py-1 rounded-full font-medium flex-shrink-0 ${e.reconnected ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                {e.reconnected ? 'Auto-reconnected' : 'Stayed disconnected'}
              </span>
            </div>
          ))
        )}
      </div>
    </motion.div>
  );
}

const PLATFORM_STYLES = {
  twitch: { label: 'Twitch', color: '#9146FF', bg: 'bg-[#9146FF]/15', border: 'border-[#9146FF]/30', text: 'text-[#A970FF]' },
  youtube: { label: 'YouTube', color: '#FF0000', bg: 'bg-red-500/15', border: 'border-red-500/30', text: 'text-red-400' },
};

function LiveAlerts({ guildId }) {
  const [channels, setChannels] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [platform, setPlatform] = useState('twitch');
  const [handle, setHandle] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [channelId, setChannelId] = useState('');
  const [template, setTemplate] = useState('');
  const [notifyLive, setNotifyLive] = useState(true);
  const [notifyUploads, setNotifyUploads] = useState(false);
  const [uploadTemplate, setUploadTemplate] = useState('');
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [status, setStatus] = useState(null);

  const loadAlerts = useCallback(() => {
    axios.get(`${API}/api/live-alerts/${guildId}`, authHeader())
      .then(r => setAlerts(r.data.alerts || []))
      .catch(() => setAlerts([]));
  }, [guildId]);

  useEffect(() => {
    axios.get(`${API}/api/announce/channels/${guildId}`, authHeader())
      .then(r => {
        const chs = r.data.channels || [];
        setChannels(chs);
        setChannelId(chs[0]?.id || '');
      })
      .catch(() => setChannels([]));
    loadAlerts();
  }, [guildId, loadAlerts]);

  const liveOn = platform === 'twitch' ? true : notifyLive;
  const uploadsOn = platform === 'youtube' && notifyUploads;
  const canSubmit = handle.trim() && channelId && (liveOn || uploadsOn);

  const submit = async () => {
    if (!canSubmit) return;
    setCreating(true);
    setStatus(null);
    try {
      await axios.post(`${API}/api/live-alerts`, {
        guild_id: guildId, platform, handle: handle.trim(),
        display_name: displayName.trim() || undefined,
        channel_id: channelId, message_template: template.trim() || undefined,
        notify_live: liveOn, notify_uploads: uploadsOn,
        upload_message_template: uploadTemplate.trim() || undefined,
      }, authHeader());
      setStatus({ ok: true, msg: 'Alert configured! BeatMetrics will post automatically.' });
      toast.success('Live alert configured');
      setHandle('');
      setDisplayName('');
      setTemplate('');
      setUploadTemplate('');
      loadAlerts();
    } catch (err) {
      setStatus({ ok: false, msg: err.response?.data?.detail || 'Could not save this alert.' });
      toast.error(err.response?.data?.detail || 'Could not save this alert.');
    } finally {
      setCreating(false);
      setTimeout(() => setStatus(null), 5000);
    }
  };

  const removeAlert = async (id) => {
    setRemoving(id);
    try {
      await axios.delete(`${API}/api/live-alerts/${id}`, { ...authHeader(), params: { guild_id: guildId } });
      setAlerts(prev => prev.filter(a => a.id !== id));
      toast.success('Alert removed');
    } catch (e) {
      toast.error('Failed to remove alert');
    } finally {
      setRemoving(null);
    }
  };

  const [testingId, setTestingId] = useState(null);
  const testAlert = async (id) => {
    setTestingId(id);
    try {
      await axios.post(`${API}/api/live-alerts/${id}/test`, null, { ...authHeader(), params: { guild_id: guildId } });
      toast.success('Test alert sent! Check your Discord channel.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test alert');
    } finally {
      setTestingId(null);
    }
  };

  const [checkingId, setCheckingId] = useState(null);
  const checkAlertNow = async (id) => {
    setCheckingId(id);
    try {
      await axios.post(`${API}/api/live-alerts/${id}/check-now`, null, { ...authHeader(), params: { guild_id: guildId } });
      toast.success('Checked! Posted to the channel if there\'s a new video or live stream.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Check failed');
    } finally {
      setCheckingId(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }}
      className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden mb-6"
      data-testid="live-alerts-card"
    >
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-[var(--border)]">
        <Radio size={15} className="text-[#5865F2]" />
        <span className="text-sm font-semibold text-white font-[Outfit]">Live Alerts</span>
        <span className="text-xs text-zinc-600 font-[Manrope] ml-1">— auto-announce when a creator goes live</span>
      </div>

      <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Builder */}
        <div className="space-y-3">
          <div className="flex gap-2">
            {['twitch', 'youtube'].map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setPlatform(p)}
                data-testid={`live-alert-platform-${p}`}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold font-[Outfit] border transition-all ${
                  platform === p
                    ? `${PLATFORM_STYLES[p].bg} ${PLATFORM_STYLES[p].border} ${PLATFORM_STYLES[p].text}`
                    : 'bg-[var(--bg-hover)] border-[var(--border)] text-zinc-500 hover:text-zinc-300'
                }`}
              >
                {PLATFORM_STYLES[p].label}
              </button>
            ))}
          </div>
          <input
            value={handle}
            onChange={e => setHandle(e.target.value)}
            placeholder={platform === 'twitch' ? 'Twitch username (e.g. ninja)' : 'YouTube channel ID (starts with UC...)'}
            data-testid="live-alert-handle-input"
            className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all font-mono-stats"
          />
          <input
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            placeholder="Display name (optional, defaults to handle)"
            data-testid="live-alert-display-name-input"
            className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
          />
          {channels === null ? (
            <p className="text-zinc-600 text-sm font-[Manrope]">Loading channels...</p>
          ) : (
            <select
              value={channelId}
              onChange={e => setChannelId(e.target.value)}
              data-testid="live-alert-channel-select"
              className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope]"
            >
              {channels.map(ch => (
                <option key={ch.id} value={ch.id} className="bg-[var(--bg-surface)]">#{ch.name}</option>
              ))}
            </select>
          )}

          {platform === 'youtube' && (
            <div className="space-y-2 p-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)]">
              <p className="text-xs text-zinc-500 uppercase tracking-widest font-[Manrope]">Notify when...</p>
              <label className="flex items-center gap-2 text-sm text-zinc-300 font-[Manrope] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={notifyLive}
                  onChange={e => setNotifyLive(e.target.checked)}
                  data-testid="live-alert-notify-live-toggle"
                  className="rounded border-white/20 bg-[var(--bg-hover)] accent-[#5865F2]"
                />
                They go live
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-300 font-[Manrope] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={notifyUploads}
                  onChange={e => setNotifyUploads(e.target.checked)}
                  data-testid="live-alert-notify-uploads-toggle"
                  className="rounded border-white/20 bg-[var(--bg-hover)] accent-[#5865F2]"
                />
                They upload a new video
              </label>
            </div>
          )}

          {liveOn && (
            <textarea
              value={template}
              onChange={e => setTemplate(e.target.value)}
              placeholder="Custom 'live' message (optional) — use {streamer}, {title}, {url}"
              rows={2}
              data-testid="live-alert-template-input"
              className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope] resize-none"
            />
          )}
          {uploadsOn && (
            <textarea
              value={uploadTemplate}
              onChange={e => setUploadTemplate(e.target.value)}
              placeholder="Custom 'new upload' message (optional) — use {streamer}, {title}, {url}"
              rows={2}
              data-testid="live-alert-upload-template-input"
              className="w-full bg-[var(--bg-hover)] border border-[var(--border)] rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2]/50 transition-all font-[Manrope] resize-none"
            />
          )}
          <button
            onClick={submit}
            disabled={!canSubmit || creating}
            data-testid="live-alert-create-btn"
            className="w-full px-5 py-2.5 bg-[#5865F2] hover:bg-[#4752C4] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-all font-[Outfit] flex items-center justify-center gap-2"
          >
            {creating
              ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</>
              : <><Radio size={14} />Add Alert</>
            }
          </button>
          <FeedbackBanner status={status} testId="live-alert-feedback" />
          <p className="text-xs text-zinc-600 font-[Manrope]">
            Checked every 3 minutes. Requires the bot host to have <code className="text-zinc-500">TWITCH_CLIENT_ID</code>/<code className="text-zinc-500">TWITCH_CLIENT_SECRET</code> and/or <code className="text-zinc-500">YOUTUBE_API_KEY</code> configured.
          </p>
        </div>

        {/* Active alerts */}
        <div>
          <p className="text-xs text-zinc-500 uppercase tracking-widest mb-2 font-[Manrope]">Configured Alerts</p>
          {alerts === null ? (
            <p className="text-zinc-600 text-sm font-[Manrope]">Loading...</p>
          ) : alerts.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-10 font-[Manrope] bg-[var(--bg-hover)] rounded-xl">No live alerts configured yet.</p>
          ) : (
            <div className="space-y-2 max-h-[26rem] overflow-y-auto pr-1">
              {alerts.map(a => {
                const style = PLATFORM_STYLES[a.platform] || PLATFORM_STYLES.twitch;
                return (
                  <div key={a.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)]" data-testid={`live-alert-${a.id}`}>
                    <span className={`text-[10px] px-2 py-1 rounded-full font-semibold font-[Manrope] flex-shrink-0 ${style.bg} ${style.text} border ${style.border}`}>
                      {style.label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-[Manrope] truncate">{a.display_name || a.handle}</p>
                      <p className="text-xs text-zinc-500 font-mono-stats truncate">
                        {a.handle} {a.is_live && <span className="ml-1.5 text-emerald-400">● live now</span>}
                      </p>
                      <p className="text-[10px] text-zinc-600 font-[Manrope] mt-0.5">
                        {[a.notify_live !== false && 'Live alerts', a.notify_uploads && 'New upload alerts'].filter(Boolean).join(' • ') || 'No alerts enabled'}
                      </p>
                    </div>
                    <button
                      onClick={() => testAlert(a.id)}
                      disabled={testingId === a.id}
                      title="Send a test alert to the configured channel"
                      data-testid={`live-alert-test-${a.id}`}
                      className="text-zinc-500 hover:text-emerald-400 disabled:opacity-50 transition-colors p-1.5 flex-shrink-0"
                    >
                      {testingId === a.id ? <div className="w-3.5 h-3.5 border-2 border-zinc-600 border-t-transparent rounded-full animate-spin" /> : <Send size={14} />}
                    </button>
                    {a.platform === 'youtube' && (
                      <button
                        onClick={() => checkAlertNow(a.id)}
                        disabled={checkingId === a.id}
                        title="Check now using real YouTube data and post if there's a new video/live stream"
                        data-testid={`live-alert-check-${a.id}`}
                        className="text-zinc-500 hover:text-blue-400 disabled:opacity-50 transition-colors p-1.5 flex-shrink-0"
                      >
                        {checkingId === a.id ? <div className="w-3.5 h-3.5 border-2 border-zinc-600 border-t-transparent rounded-full animate-spin" /> : <RefreshCw size={14} />}
                      </button>
                    )}
                    <button
                      onClick={() => removeAlert(a.id)}
                      disabled={removing === a.id}
                      data-testid={`live-alert-remove-${a.id}`}
                      className="text-zinc-500 hover:text-red-400 disabled:opacity-50 transition-colors p-1.5 flex-shrink-0"
                    >
                      {removing === a.id ? <div className="w-3.5 h-3.5 border-2 border-zinc-600 border-t-transparent rounded-full animate-spin" /> : <Trash2 size={14} />}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-xs">
      <p className="text-zinc-400 mb-1">{label}</p>
      {payload.map(p => <p key={p.dataKey} style={{ color: p.color }}>{p.name}: {p.value}</p>)}
    </div>
  );
};

function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="stat-card"
      style={{ borderLeft: `2px solid ${color}55` }}
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-2 font-[Manrope]">{label}</p>
          <p className="text-4xl font-mono-stats font-bold" style={{ color }}>{value ?? '—'}</p>
        </div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: color + '22' }}>
          <Icon size={20} style={{ color }} />
        </div>
      </div>
    </motion.div>
  );
}

function ActionBadge({ action }) {
  const styles = {
    ban: 'bg-red-500/15 text-red-400 border border-red-500/30',
    kick: 'bg-amber-500/15 text-amber-400 border border-amber-500/30',
    warn: 'bg-blue-500/15 text-blue-400 border border-blue-500/30',
    timeout: 'bg-purple-500/15 text-purple-400 border border-purple-500/30',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium font-[Manrope] ${styles[action] || 'bg-zinc-800 text-zinc-400'}`}>
      {action}
    </span>
  );
}

export default function Moderation() {
  const { selectedGuildId, selectedGuild } = useGuild();
  const isStaff = !!selectedGuild?.is_staff;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(null);
  const [logSearch, setLogSearch] = useState('');
  const [logActionFilter, setLogActionFilter] = useState('all');

  const loadLog = useCallback(() => {
    if (!selectedGuildId) return;
    setLoading(true);
    axios.get(`${API}/api/moderation/log`, { ...authHeader(), params: { guild_id: selectedGuildId } })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedGuildId]);

  useEffect(() => { loadLog(); }, [loadLog]);

  const clearWarning = async (id) => {
    setClearing(id);
    try {
      await axios.post(`${API}/api/moderation/warnings/${id}/clear`, {}, { ...authHeader(), params: { guild_id: selectedGuildId } });
      setData(prev => ({
        ...prev,
        actions: prev.actions.map(a => a.id === id ? { ...a, cleared: true, cleared_by: 'You', cleared_at: new Date().toISOString() } : a),
      }));
    } catch (e) {
      // ignore
    } finally {
      setClearing(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Sidebar />
        <main className="lg:ml-60 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-10 h-10 border-2 border-[#5865F2] border-t-transparent rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  const stats = data?.stats || {};
  const allActions = data?.actions || [];
  const filteredActions = allActions.filter(a => {
    if (logActionFilter !== 'all' && a.action !== logActionFilter) return false;
    const q = logSearch.trim().toLowerCase();
    if (!q) return true;
    return [a.target_username, a.target_id, a.moderator_username]
      .some(field => (field || '').toString().toLowerCase().includes(q));
  });

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Sidebar />
      <main className="lg:ml-60 pt-16 lg:pt-0 p-6" data-testid="moderation-main">
        <div className="mb-8">
          <h1 className="text-3xl font-bold font-[Outfit] text-white">Moderation</h1>
          <p className="text-zinc-500 text-sm mt-1 font-[Manrope]">Track and review all moderation actions</p>
        </div>

        {/* Dashboard actions: moderate members and send announcements without slash commands */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <TakeActionForm guildId={selectedGuildId} />
          {isStaff && <AnnouncementForm guildId={selectedGuildId} />}
        </div>

        {isStaff && <EmbedStudio guildId={selectedGuildId} />}
        {isStaff && <ReactionRolesBuilder guildId={selectedGuildId} />}
        <SuggestionsBoard guildId={selectedGuildId} />
        <BanAppealsPanel guildId={selectedGuildId} />
        <BotVoiceHealth guildId={selectedGuildId} />
        <LiveVoiceMonitor guildId={selectedGuildId} />
        {isStaff && <LiveAlerts guildId={selectedGuildId} />}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard icon={Shield} label="Total Actions" value={stats.total || 0} color="#5865F2" testId="stat-total-actions" />
          <StatCard icon={Ban} label="Bans" value={stats.bans || 0} color="#EF4444" testId="stat-bans" />
          <StatCard icon={UserX} label="Kicks" value={stats.kicks || 0} color="#F59E0B" testId="stat-kicks" />
          <StatCard icon={AlertTriangle} label="Warns" value={stats.warns || 0} color="#FBBF24" testId="stat-warns" />
          <StatCard icon={Clock} label="Timeouts" value={stats.timeouts || 0} color="#8B5CF6" testId="stat-timeouts" />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
          {/* Daily actions */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="lg:col-span-3 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
            data-testid="mod-actions-chart"
          >
            <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Actions Over Time</h3>
            <p className="text-xs text-zinc-500 mb-4 font-[Manrope]">Last 14 days</p>
            {(data?.daily_chart || []).every(d => d.actions === 0) ? (
              <p className="text-zinc-500 text-sm text-center py-12">No moderation actions yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={data?.daily_chart || []}>
                  <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="date" tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={false} interval={2} />
                  <YAxis tick={{ fill: '#71717A', fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="actions" name="Actions" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </motion.div>

          {/* Type distribution */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="lg:col-span-2 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5"
            data-testid="mod-type-chart"
          >
            <h3 className="text-sm font-semibold text-white mb-1 font-[Outfit]">Action Types</h3>
            <p className="text-xs text-zinc-500 mb-2 font-[Manrope]">Distribution</p>
            {(data?.by_type || []).length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-12">No data</p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={140}>
                  <PieChart>
                    <Pie data={data.by_type} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={55} innerRadius={30}>
                      {data.by_type.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={({ active, payload }) => active && payload?.[0] ? (
                      <div className="bg-[var(--bg-surface)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-xs">
                        <p className="text-zinc-400">{payload[0].name}</p>
                        <p className="text-white font-bold">{payload[0].value}</p>
                      </div>
                    ) : null} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1 mt-1">
                  {data.by_type.map((t, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-zinc-400 capitalize flex-1">{t.type}</span>
                      <span className="text-zinc-300 font-mono-stats">{t.count}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        </div>

        {/* Actions Log Table */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden"
          data-testid="mod-log-table"
        >
          <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white font-[Outfit]">Moderation Log</h3>
            <span className="text-xs text-zinc-500 font-mono-stats">{filteredActions.length} of {allActions.length} actions</span>
          </div>
          <div className="px-5 py-3 border-b border-[var(--border)] flex flex-col sm:flex-row gap-2.5" data-testid="mod-log-filter-bar">
            <input
              type="text"
              value={logSearch}
              onChange={e => setLogSearch(e.target.value)}
              placeholder="Search by target user, user ID, or moderator..."
              data-testid="mod-log-search-input"
              className="flex-1 bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#5865F2] transition-colors font-[Manrope]"
            />
            <select
              value={logActionFilter}
              onChange={e => setLogActionFilter(e.target.value)}
              data-testid="mod-log-action-filter"
              className="bg-[var(--bg-base)] border border-[var(--border-md)] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#5865F2] transition-colors font-[Manrope]"
            >
              <option value="all">All Actions</option>
              <option value="ban">Bans</option>
              <option value="kick">Kicks</option>
              <option value="warn">Warns</option>
              <option value="timeout">Timeouts</option>
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border)]">
                  <th className="text-left px-5 py-3 text-xs text-zinc-500 font-semibold uppercase tracking-wider">Action</th>
                  <th className="text-left px-5 py-3 text-xs text-zinc-500 font-semibold uppercase tracking-wider">Target</th>
                  <th className="text-left px-5 py-3 text-xs text-zinc-500 font-semibold uppercase tracking-wider hidden md:table-cell">Moderator</th>
                  <th className="text-left px-5 py-3 text-xs text-zinc-500 font-semibold uppercase tracking-wider hidden lg:table-cell">Reason</th>
                  <th className="text-left px-5 py-3 text-xs text-zinc-500 font-semibold uppercase tracking-wider">Time</th>
                  <th className="text-left px-5 py-3 text-xs text-zinc-500 font-semibold uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {allActions.length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-8 text-center text-zinc-500">No moderation actions recorded. Use /ban, /kick, /warn, or /timeout in Discord.</td></tr>
                ) : filteredActions.length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-8 text-center text-zinc-500">No actions match your search/filter.</td></tr>
                ) : (
                  filteredActions.map((a, i) => (
                    <tr key={i} className="border-b border-[var(--border)] hover:bg-[var(--bg-hover)] transition-colors" data-testid={`mod-log-row-${i}`}>
                      <td className="px-5 py-3">
                        <ActionBadge action={a.action} />
                        {a.action === 'warn' && typeof a.dm_delivered === 'boolean' && (
                          <span
                            className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full font-medium font-[Manrope] ${
                              a.dm_delivered
                                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                                : 'bg-zinc-700/30 text-zinc-400 border border-zinc-600/30'
                            }`}
                            title={a.dm_delivered ? 'The member received this warning via DM' : 'The member could not be DM\'d (DMs closed)'}
                          >
                            {a.dm_delivered ? 'DM sent' : 'DM failed'}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-white font-[Manrope]">{a.target_username || a.target_id}</td>
                      <td className="px-5 py-3 text-zinc-400 hidden md:table-cell">{a.moderator_username || '—'}</td>
                      <td className="px-5 py-3 text-zinc-400 hidden lg:table-cell max-w-xs truncate">{a.reason || '—'}</td>
                      <td className="px-5 py-3 text-zinc-500 font-mono-stats text-xs">
                        {new Date(a.timestamp).toLocaleString()}
                      </td>
                      <td className="px-5 py-3">
                        {a.action === 'warn' ? (
                          a.cleared ? (
                            <span
                              className="text-[10px] px-2 py-0.5 rounded-full font-medium font-[Manrope] bg-zinc-700/30 text-zinc-400 border border-zinc-600/30"
                              title={a.cleared_by ? `Cleared by ${a.cleared_by}` : undefined}
                            >
                              Cleared
                            </span>
                          ) : (
                            <button
                              onClick={() => clearWarning(a.id)}
                              disabled={clearing === a.id}
                              data-testid={`clear-warning-${a.id}`}
                              className="text-xs text-[#5865F2] hover:text-[#7983F5] disabled:opacity-50 transition-colors font-[Manrope]"
                            >
                              {clearing === a.id ? 'Clearing...' : 'Clear'}
                            </button>
                          )
                        ) : (
                          <span className="text-zinc-700">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
